<script setup>
/**
 * VClamp — self-contained replacement for vue-clamp.
 *
 * Clamps content to maxLines using CSS -webkit-line-clamp.
 * Detects overflow with ResizeObserver to expose `clamped` state.
 * Supports the #after slot with { toggle, expanded, clamped } — same API as vue-clamp.
 *
 * Props:
 *   tag        — root element tag (default: 'div')
 *   maxLines   — max visible lines (default: 3)
 *   maxHeight  — max height string or number in px (optional, alternative to maxLines)
 *   autoresize — re-check clamping on container resize (default: false)
 *   expanded   — controlled expanded state (v-model via update:expanded)
 *   ellipsis   — kept for API compatibility; ellipsis is added by the browser
 */
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';

const props = defineProps({
    tag:       { type: String,  default: 'div' },
    maxLines:  { type: Number,  default: 3 },
    maxHeight: { default: null },
    autoresize:{ type: Boolean, default: false },
    expanded:  { type: Boolean, default: false },
    ellipsis:  { type: String,  default: '…' },
});

const emit = defineEmits(['update:expanded', 'clampchange']);

const localExpanded = ref(props.expanded);
const isClamped     = ref(false);
const contentEl     = ref(null);

let observer = null;

const clampStyle = computed(() => {
    if (localExpanded.value) return { overflow: 'visible' };

    if (props.maxHeight !== null) {
        const h = typeof props.maxHeight === 'number' ? props.maxHeight + 'px' : props.maxHeight;
        return { overflow: 'hidden', maxHeight: h };
    }

    return {
        display:         '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: props.maxLines,
        overflow:        'hidden',
    };
});

function checkClamped() {
    if (!contentEl.value) return;
    // When expanded the content is never clamped — skip DOM measurement.
    const clamped = !localExpanded.value && contentEl.value.scrollHeight > contentEl.value.clientHeight + 1;
    if (clamped !== isClamped.value) {
        isClamped.value = clamped;
        emit('clampchange', clamped);
    }
}

function toggle() {
    localExpanded.value = !localExpanded.value;
    emit('update:expanded', localExpanded.value);
    nextTick(checkClamped);
}

function expand()   { if (!localExpanded.value) toggle(); }
function collapse() { if (localExpanded.value)  toggle(); }

watch(() => props.expanded, val => { localExpanded.value = val; });

watch([() => props.maxLines, () => props.maxHeight, localExpanded], () => {
    nextTick(checkClamped);
});

onMounted(() => {
    nextTick(checkClamped);

    if (props.autoresize && contentEl.value && window.ResizeObserver) {
        observer = new ResizeObserver(checkClamped);
        observer.observe(contentEl.value);
    }
});

onBeforeUnmount(() => {
    if (observer) observer.disconnect();
});
</script>

<template>
    <component :is="tag">
        <span ref="contentEl" :style="clampStyle">
            <slot />
        </span>
        <slot
            name="after"
            :expanded="localExpanded"
            :clamped="isClamped"
            :toggle="toggle"
            :expand="expand"
            :collapse="collapse"
        />
    </component>
</template>
